"use client";

export type HomeworkDiagPhoto = {
  name: string;
  type: string;
  size: number;
};

export type HomeworkDiagPayload = {
  stage: string;
  ok?: boolean;
  error?: string;
  method?: "POST" | "PATCH" | "DELETE" | "GET";
  photos?: HomeworkDiagPhoto[];
  extra?: Record<string, unknown>;
};

function telegramInitData(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const fromTelegram = window.Telegram?.WebApp?.initData;
  if (fromTelegram) return fromTelegram;
  const hash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  return new URLSearchParams(hash).get("tgWebAppData") ?? undefined;
}

function telegramPlatform(): string | undefined {
  const tg = window.Telegram?.WebApp as
    | (NonNullable<typeof window.Telegram>["WebApp"] & { platform?: string; version?: string })
    | undefined;
  return tg?.platform;
}

function telegramVersion(): string | undefined {
  const tg = window.Telegram?.WebApp as
    | (NonNullable<typeof window.Telegram>["WebApp"] & { version?: string })
    | undefined;
  return tg?.version;
}

export function summarizePhotos(files: Array<{ file: File } | File>): HomeworkDiagPhoto[] {
  return files.map((item) => {
    const file = "file" in item ? item.file : item;
    return {
      name: file.name || "(no-name)",
      type: file.type || "(empty-type)",
      size: file.size,
    };
  });
}

/** Fire-and-forget client diagnostics for homework uploads. Must stay small (no photo bytes). */
export async function reportHomeworkDiag(payload: HomeworkDiagPayload): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    const headers = new Headers({ "Content-Type": "application/json" });
    const initData = telegramInitData();
    if (initData) headers.set("Authorization", `tma ${initData}`);

    const body = JSON.stringify({
      ...payload,
      at: new Date().toISOString(),
      online: navigator.onLine,
      ua: navigator.userAgent,
      platform: telegramPlatform() ?? null,
      tgVersion: telegramVersion() ?? null,
      href: window.location.href,
    });

    await fetch("/api/homework/diag", {
      method: "POST",
      headers,
      body,
      credentials: "include",
      keepalive: true,
    });
  } catch {
    // Never block the teacher UX on diagnostics.
  }
}

export function describeFetchError(error: unknown): string {
  if (error instanceof TypeError) {
    return `Failed to fetch (${error.message || "TypeError"})`;
  }
  if (error instanceof Error) return error.message;
  return String(error);
}
